<template>
  <Card>
    <template #title>
      <div class="p-d-flex p-justify-between">
        <span
          >แบบประเมินพนักงานใหม่/พนักงานย้ายแผนก/พนักงานได้เลื่อนตำแหน่ง
        </span>
        <div>
          <Button
            label="สร้างใหม่"
            icon="pi pi-plus"
            @click="onCreatePositionDocument"
          ></Button>
          <back-button class="p-ml-2" />
        </div>
      </div>
    </template>

    <template #content="">
      <div class="p-grid">
        <div class="p-col-12 p-lg-3 p-md-4 p-fluid p-field ">
          <span class="p-float-label">
            <Calendar
              id="selectedDate"
              v-model="selectedDate"
              selectionMode="range"
            ></Calendar>
            <label for="selectedDate">ระบุวันที่</label>
          </span>
        </div>
        <div class="p-col-fixed">
          <Button label="ค้นหา" icon="pi pi-search" @click="onSearch"></Button>
        </div>
        <div class="p-col-12">
          <DataTable
            :value="positionDocuments"
            class="p-datatable-sm"
            paginator
            :rows="10"
            v-model:filters="filters"
            filterDisplay="row"
          >
            <template #empty="">ไม่พบข้อมูล</template>
            <Column header="รหัสเอกสาร" field="id" sortable>
              <template #filter="{filterModel, filterCallback}">
                <InputText
                  v-model="filterModel.value"
                  @input="filterCallback"
                ></InputText>
              </template>
            </Column>
            <Column header="พนักงาน" field="employee.id" sortable>
              <template #body="props">
                {{ props.data.employeeId }} - {{ props.data.employee.prename
                }}{{ props.data.employee.name }}
                {{ props.data.employee.surname }}
              </template>
              <template #filter="{filterModel, filterCallback}">
                <InputUser
                  showClear
                  v-model="filterModel.value"
                  @change="filterCallback"
                ></InputUser>
              </template>
            </Column>
            <Column
              header="Division"
              field="employee.divisionDescription"
              sortable
            >
              <template #filter="{filterModel, filterCallback}">
                <InputText
                  v-model="filterModel.value"
                  @input="filterCallback"
                ></InputText>
              </template>
            </Column>
            <Column
              header="Department"
              field="employee.departmentDescription"
              sortable
            >
              <template #filter="{filterModel, filterCallback}">
                <InputText
                  v-model="filterModel.value"
                  @input="filterCallback"
                ></InputText>
              </template>
            </Column>
            <Column header="วันที่เอกสาร" field="documentDate" sortable>
              <template #body="props">
                {{ formatAppDate(props.data.documentDate) }}
              </template>
            </Column>
            <Column header="ประเภท" field="type" sortable>
              <template #body="props">
                {{
                  positionDocumentTypes.find((x) => x.code === props.data.type)
                    ?.description ?? "N/A"
                }}
              </template>
            </Column>
            <Column header="Release?" field="releaseStatus">
              <template #body="props">
                <ReleaseStatusBadge :status="props.data.releaseStatus" />
              </template>
              <template #filter="{filterModel, filterCallback}">
                <Dropdown
                  v-model="filterModel.value"
                  :options="releases"
                  @change="filterCallback"
                  showClear
                ></Dropdown>
              </template>
            </Column>
            <Column header="Status" field="status">
              <template #body="props">
                <StatusBadge :status="props.data.status"></StatusBadge>
              </template>
              <template #filter="{filterModel, filterCallback}">
                <Dropdown
                  v-model="filterModel.value"
                  :options="statuses"
                  @change="filterCallback"
                  showClear
                ></Dropdown>
              </template>
            </Column>
            <Column headerStyle="width:7rem" bodyStyle="width:7rem">
              <template #body="props">
                <Button
                  icon="pi pi-eye"
                  class="p-ml-2 p-button-rounded p-button-success"
                  v-if="props.data.releaseOn"
                  @click="onViewDocument(props.data.id)"
                ></Button>
                <Button
                  icon="pi pi-pencil"
                  class="p-ml-2 p-button-rounded"
                  v-if="!props.data.releaseOn"
                  @click="onEditDocument(props.data.id)"
                ></Button>
                <Button
                  icon="pi pi-copy"
                  class="p-ml-2 p-button-danger p-button-rounded"
                  @click="onCopyDocument(props.data.id)"
                ></Button>
              </template>
            </Column>
          </DataTable>
        </div>
      </div>
    </template>
  </Card>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed } from "vue";
import { usePlugin } from "@/plugins";
import {
  PositionDocument,
  GetPositionDocumentResponse,
  GetPositionDocumentRequest,
  positionDocumentTypes,
} from "@/types";
import BackButton from "@/components/BackButton.vue";
import StatusBadge from "@/components/StatusBadge.vue";
import InputUser from "@/components/InputUser.vue";
import ReleaseStatusBadge from "@/components/ReleaseStatusBadge.vue";
import { FilterMatchMode, FilterOperator } from "primevue/api";

export default defineComponent({
  name: "PositionDocumentList",
  components: { BackButton, StatusBadge, ReleaseStatusBadge, InputUser },
  setup() {
    const {
      setLoading,
      formatAppDate,
      onRouterPush,
      error,
      axios,
      success,
      dayjs,
      confirmation,
    } = usePlugin();

    const filters = ref({
      id: { value: null, matchMode: FilterMatchMode.CONTAINS },
      "employee.id": {
        value: null,
        matchMode: FilterMatchMode.EQUALS,
      },
      "employee.divisionDescription": {
        value: null,
        matchMode: FilterMatchMode.CONTAINS,
      },
      "employee.departmentDescription": {
        value: null,
        matchMode: FilterMatchMode.CONTAINS,
      },
      releaseStatus: {
        value: null,
        matchMode: FilterMatchMode.EQUALS,
      },
      status: {
        value: null,
        matchMode: FilterMatchMode.EQUALS,
      },
    });

    const selectedDate = ref<Date[]>([
      dayjs()
        .startOf("month")
        .toDate(),
      dayjs()
        .endOf("month")
        .toDate(),
    ]);
    const positionDocuments = ref<PositionDocument[]>([]);
    const onEditDocument = (id: string) => {
      onRouterPush({ name: "editPositionDocument", params: { id: id } });
    };
    const onViewDocument = (id: string) => {
      onRouterPush({ name: "positionAssessment", params: { id: id } });
    };
    const onCreatePositionDocument = () => {
      onRouterPush({ name: "createPositionDocument" });
    };

    const onCopyDocument = (id: number) => {
      confirmation.require({
        header: "คัดลอกข้อมูล",
        message: "กรุณายืนยันเพื่อทำการคัดลอกข้อมูลเพื่อสร้างเอกสารใบใหม่?",
        acceptLabel: "คัดลอกข้อมูล",
        accept: () => {
          onRouterPush({
            name: "editPositionDocument",
            params: { id: id, copy: "true" },
          });
        },
        rejectLabel: "ยกเลิก",
      });
    };

    const loadDocument = async () => {
      setLoading(true);
      try {
        const result = await axios.post<GetPositionDocumentResponse>(
          "api/v1/services/positionDocument/get",
          {
            fromDate: selectedDate.value[0],
            toDate: selectedDate.value[1],
          } as GetPositionDocumentRequest
        );

        positionDocuments.value = result.data.positionDocuments ?? [];
        success("ดึงข้อมูลสำเร็จแล้ว");
      } catch (e) {
        error(e);
      } finally {
        setLoading(false);
      }
    };

    const onSearch = () => {
      loadDocument();
    };

    const releases = computed(() => [
      ...new Set(positionDocuments.value.map((x) => x.releaseStatus)),
    ]);
    const statuses = computed(() => [
      ...new Set(positionDocuments.value.map((x) => x.status)),
    ]);

    onMounted(() => loadDocument());

    return {
      positionDocuments,
      formatAppDate,
      onEditDocument,
      onCopyDocument,
      onViewDocument,
      onCreatePositionDocument,
      positionDocumentTypes,
      selectedDate,
      onSearch,
      filters,
      releases,
      statuses,
    };
  },
});
</script>

<style></style>
